import { getQueryParam } from '@mi-front/core';
import { Box } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { YoutubePanoramaCards } from '../../../stream/youtube/panorama/YoutubePanoramaCards';
import {
  STATUS_FETCH_SEGMENTS_MARGIN_PX,
  STREAM_HEIGHT,
  STREAM_HEIGHT_COMPACT,
} from '../../constants';
import { useStatusViewContext } from '../../context/StatusViewContext';
import { StatusNoActivityMessage } from './activity/StatusNoActivityMessage';
import { StatusHeader } from './header/StatusHeader';
import { StatusWaveformLoader } from './loading/StatusWaveformLoader';
import { StatusPreview } from './preview/StatusPreview';
import { StatusCard } from './StatusCard';
import { useStatusSegments } from './useStatusSegments';
import { StationStatus as StationStatusData } from '../../types';
import { Station } from '@app/core/types/stations';

interface Props {
  readonly station: Station;
  readonly status: StationStatusData;
}

/**
 * Station status card component that displays station information and waveform.
 * The entire card is clickable and navigates to the station's stream page.
 */
export function StationStatus({ station, status }: Props) {
  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: STATUS_FETCH_SEGMENTS_MARGIN_PX,
  });

  const { compact } = useStatusViewContext();
  const { segments, previewRange, isLoading } = useStatusSegments({
    station,
    status,
    enabled: inView,
    compact,
  });

  let content;

  const isOffline = status.status !== 'online' && !getQueryParam('offline_preview');

  if (isLoading || !inView) {
    content = <StatusWaveformLoader />;
  } else if (
    station.youtubeNative &&
    ((!segments.length && isOffline) || getQueryParam('offline_preview'))
  ) {
    content = (
      <YoutubePanoramaCards
        station={station}
        compact={compact}
        height={compact ? STREAM_HEIGHT_COMPACT : STREAM_HEIGHT}
      />
    );
  } else if (!segments.length || getQueryParam('offline_preview')) {
    content = <StatusNoActivityMessage />;
  } else {
    content = <StatusPreview station={station} segments={segments} range={previewRange} />;
  }

  return (
    <StatusCard station={station} innerRef={ref}>
      <StatusHeader
        station={station}
        isOnline={status.status === 'online' && !getQueryParam('offline_preview')}
      />
      <Box
        sx={{
          overflow: 'hidden',
          mt: compact ? 0.5 : 1,
          mx: compact ? -1.5 : -2,
        }}
      >
        {content}
      </Box>
    </StatusCard>
  );
}
